import {
  normalizeStatusToken,
  parseDateRangeCell,
  type AvailabilityProfile,
  type AvailabilityWindowState,
} from '@ooh/contracts';
import type { InterpretedRow } from '../components/interpreted-rows-table';

/**
 * Per-row match info the caller derives from the initial server preview
 * response (`AvailabilityImportPreviewRow.matchedUnitId`), overlaid with any
 * debounced `POST /rematch` response for edits to matching-relevant fields
 * (`idColumns.supplierNumberColumn`, `idColumns.grwIdColumn`,
 * `supplierColumn`, `defaultSupplierName` — see `hooks/use-rematched-rows.ts`
 * and `preview-phase.tsx`'s `mergeRematchedRows`). Matching itself is always
 * a server/DB concern — this is never recomputed purely client-side.
 */
export interface RowMatchSeed {
  matched: boolean;
}

/** Best-effort human-readable row identifier: grwId column, else supplier-number column, else ''. */
export function pickRawIdentifier(
  profile: AvailabilityProfile,
  row: Record<string, string>,
): string {
  const grwCol = profile.idColumns.grwIdColumn;
  const supCol = profile.idColumns.supplierNumberColumn;
  if (grwCol && row[grwCol]) return row[grwCol];
  if (supCol && row[supCol]) return row[supCol];
  return '';
}

function statusFor(
  profile: AvailabilityProfile,
  row: Record<string, string>,
): AvailabilityWindowState | null {
  // No statusColumn: reuse the same normalizeStatusToken('', profile) call
  // the has-a-statusColumn branch below already makes — it unconditionally
  // resolves a blank/empty raw value to FREE (checked before `polarity`),
  // so preview matches what the worker's write path actually writes for a
  // list_of_busy profile with no status column.
  const rawValue = profile.statusColumn ? (row[profile.statusColumn] ?? '') : '';
  const result = normalizeStatusToken(rawValue, profile);
  return 'state' in result ? result.state : null;
}

function periodForGlobal(profile: AvailabilityProfile): string | null {
  return profile.globalPeriod
    ? `${profile.globalPeriod.start} – ${profile.globalPeriod.end}`
    : null;
}

/** Uses the shared `parseDateRangeCell` (`@ooh/contracts`) — the same
 * override-lookup-then-flexible-parse rule the write path applies
 * (`packages/db/src/queries/availability-windows/interpret-row.ts`) — so a
 * cell the worker would actually reject as unparseable never renders a
 * confident period here. */
function periodForDateRange(
  profile: AvailabilityProfile,
  row: Record<string, string>,
): string | null {
  if (!profile.dateRangeColumns) return null;
  const { startColumn, endColumn } = profile.dateRangeColumns;
  const from = parseDateRangeCell(profile.dateFormatOverrides, row[startColumn] ?? '', startColumn);
  const to = parseDateRangeCell(profile.dateFormatOverrides, row[endColumn] ?? '', endColumn);
  if (!from || !to || from > to) return null;
  return `${from} – ${to}`;
}

function periodForLabelMap(
  profile: AvailabilityProfile,
  row: Record<string, string>,
): string | null {
  if (!profile.periodLabelColumn) return null;
  const label = row[profile.periodLabelColumn];
  const mapping = label ? profile.periodLabelMap?.[label] : undefined;
  return mapping ? `${mapping.start} – ${mapping.end}` : null;
}

/** month/half_month: the period-columns table is the editable surface for
 * those periods, not a per-row recompute here (no spec scenario needs it). */
function periodFor(profile: AvailabilityProfile, row: Record<string, string>): string | null {
  switch (profile.timeEncoding) {
    case 'global':
      return periodForGlobal(profile);
    case 'date_range':
      return periodForDateRange(profile, row);
    case 'period_label_map':
      return periodForLabelMap(profile, row);
    default:
      return null;
  }
}

/**
 * Derives the editable preview's interpreted rows from the CURRENT (possibly
 * edited) profile applied against the upload's sample rows. See this file's
 * doc comment header for why this is client-derived rather than a
 * server round-trip.
 */
export function deriveInterpretedRows(
  profile: AvailabilityProfile,
  sampleRows: Record<string, string>[],
  matchSeeds: RowMatchSeed[],
): InterpretedRow[] {
  return sampleRows.map((row, idx) => ({
    id: `row-${idx}`,
    unit: pickRawIdentifier(profile, row) || `#${idx + 1}`,
    matched: matchSeeds[idx]?.matched ?? false,
    period: periodFor(profile, row),
    status: statusFor(profile, row),
  }));
}
